/**
 * Berndorf Horizontal Carousel
 *
 * Wires up every `[data-berndorf-carousel]` container:
 *   - prev/next arrows scroll the track by roughly one viewport width
 *   - arrows are hidden at the start/end of the scroll range
 *   - also re-initialises on Shopify section:load (theme editor)
 *
 * Das Ziehen mit der Maus steckt **nicht** hier, sondern in
 * `berndorf-drag-scroll.js` — dieselbe Mechanik trägt auch die
 * Thumbnail-Leiste der Produktgalerie. Die Spur meldet sich dort über
 * `data-berndorf-drag-scroll` im Markup an; das Skript liegt global im
 * Layout.
 */
(function () {
  var cleanups = new WeakMap();

  function initOne(root) {
    if (root.dataset.berndorfCarouselReady === 'true') return;

    var track = root.querySelector('[data-berndorf-carousel-track]');
    var prev = root.querySelector('[data-berndorf-carousel-prev]');
    var next = root.querySelector('[data-berndorf-carousel-next]');
    var pagination = root.querySelector('[data-berndorf-carousel-pagination]');
    if (!track) return;
    root.dataset.berndorfCarouselReady = 'true';
    var controller = new AbortController();
    var listenerOptions = { signal: controller.signal };

    var observedImage = null;
    var resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(updateLayout) : null;

    cleanups.set(root, function () {
      if (resizeObserver) resizeObserver.disconnect();
      root.style.removeProperty('--berndorf-carousel-image-midpoint');
      controller.abort();
      delete root.dataset.berndorfCarouselReady;
      if (pagination) delete pagination.dataset.berndorfCarouselPaginationReady;
    });

    // Only overlaid product arrows follow the image. In-flow navigation keeps
    // its existing layout. Rebind after recently-viewed/tab content replacement.
    function updateLayout() {
      var image = track.querySelector('.berndorf-card__image-wrapper');
      if (image !== observedImage) {
        if (resizeObserver && observedImage) resizeObserver.unobserve(observedImage);
        observedImage = image;
        if (resizeObserver && image) resizeObserver.observe(image);
      }
      var arrow = next || prev;
      if (image && arrow && getComputedStyle(arrow).position === 'absolute') {
        var bounds = image.getBoundingClientRect();
        if (bounds.height > 0) {
          var midpoint = bounds.top - root.getBoundingClientRect().top - root.clientTop + bounds.height / 2;
          root.style.setProperty('--berndorf-carousel-image-midpoint', midpoint + 'px');
        }
      } else {
        root.style.removeProperty('--berndorf-carousel-image-midpoint');
      }
      updateControls();
    }

    function step() {
      var first = track.querySelector('li, .berndorf-carousel__slide');
      var cardWidth = first ? first.getBoundingClientRect().width : track.clientWidth * 0.5;
      var gap = parseFloat(getComputedStyle(track).columnGap || '16') || 16;
      var visible = Math.max(1, Math.floor(track.clientWidth / (cardWidth + gap)));
      return (cardWidth + gap) * visible;
    }

    function pageMetrics() {
      if (!track.clientWidth) return { pages: 0, step: 0 };
      var first = track.querySelector('li, .berndorf-carousel__slide');
      if (!first) return { pages: 0, step: 0 };
      var cardWidth = first.getBoundingClientRect().width;
      var gap = parseFloat(getComputedStyle(track).columnGap || '16') || 16;
      var visible = Math.max(1, Math.floor((track.clientWidth + gap) / (cardWidth + gap)));
      return {
        pages: Math.max(1, Math.ceil(track.children.length / visible)),
        step: (cardWidth + gap) * visible
      };
    }

    function updatePagination() {
      if (!pagination) return;
      var metrics = pageMetrics();
      if (!metrics.pages) return;

      if (pagination.children.length !== metrics.pages) {
        pagination.innerHTML = '';
        for (var index = 0; index < metrics.pages; index += 1) {
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'berndorf-carousel__page';
          button.dataset.berndorfCarouselPage = String(index);
          button.setAttribute('aria-label', 'Seite ' + (index + 1) + ' von ' + metrics.pages);
          pagination.appendChild(button);
        }
      }

      var current = metrics.step ? Math.round(track.scrollLeft / metrics.step) : 0;
      current = Math.max(0, Math.min(metrics.pages - 1, current));
      Array.prototype.forEach.call(pagination.children, function (button, index) {
        var active = index === current;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'true' : 'false');
      });
      pagination.hidden = metrics.pages <= 1;
    }

    function updateControls() {
      var max = track.scrollWidth - track.clientWidth - 1;
      if (prev) { prev.classList.toggle('is-disabled', track.scrollLeft <= 1); prev.disabled = track.scrollLeft <= 1; }
      if (next) { next.classList.toggle('is-disabled', track.scrollLeft >= max); next.disabled = track.scrollLeft >= max; }
      updatePagination();
    }

    if (prev) {
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      }, listenerOptions);
    }
    if (next) {
      next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      }, listenerOptions);
    }
    if (pagination && pagination.dataset.berndorfCarouselPaginationReady !== 'true') {
      pagination.dataset.berndorfCarouselPaginationReady = 'true';
      pagination.addEventListener('click', function (event) {
        var button = event.target.closest('[data-berndorf-carousel-page]');
        if (!button) return;
        var metrics = pageMetrics();
        var page = parseInt(button.dataset.berndorfCarouselPage || '0', 10) || 0;
        track.scrollTo({ left: metrics.step * page, behavior: 'smooth' });
      }, listenerOptions);
    }
    track.addEventListener('scroll', updateControls, { passive: true, signal: controller.signal });
    window.addEventListener('resize', updateLayout, listenerOptions);
    root.addEventListener('berndorf:carousel-refresh', updateLayout, listenerOptions);
    if (resizeObserver) resizeObserver.observe(track);
    updateLayout();
  }

  function init() {
    document.querySelectorAll('[data-berndorf-carousel]').forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-berndorf-carousel]').forEach(function (root) {
      var cleanup = cleanups.get(root);
      if (cleanup) cleanup();
    });
  });
})();
